import axios from 'axios';
import { FixcenterProduct } from '../../../shared/models/fixcenter-product.model';
import { IShopingCartProduct } from '../../order/interfaces/shopping-cart-product.interface';
import { IShopingCart } from '../../order/interfaces/shopping-cart.interface';
import { ProductPricing } from '../../../shared/models/product-pricing.model';
import { Utils } from '../../../shared/constants/utilities.constants';
import { IBuildingSiteDto } from '../../order/interfaces/building-site.interface';
import { IBuildingSiteListViewDto } from '../../order/interfaces/building-site-list-view.interface';
import { CartOrderFormViewModel } from '../../../containers/shopping-cart-container/cart-order-form/cart-order-form-view.model';
import { IShopingCartDetail } from '../../order/interfaces/shopping-cart-detail.interface';
import { IBuildingSiteAddressListView } from '../../order/interfaces/building-site-address-list-view.interface';
import { IBuyerContactListView } from '../../order/interfaces/buyer-contact-list-view.interface';
import { IProductPackagingDto } from '../../../shared/models/product-packaging-dto.model';

const prefix: string = 'ShoppingCart/';
const roundFactor: number = 2;
const netPriceRoundFactor: number = 4;
export const shopingCartAlertVisibilityTimeInMs: number = 10000;
export const shopingCartAlertMaxWidth: number = 500;

export const getShoppingCart = () => {
    return axios.get(`${prefix}GetShoppingCart`);
};

export const getBuildingSites = (dto: IBuildingSiteDto) => {
    return axios.post(`${prefix}GetBuildingSites`, dto);
};

export const getBuildingSitesAddresses = (contractorUuid: string, page: number) => {
    return axios.get(`${prefix}GetBuildingSitesAddresses?contractorUuid=${contractorUuid}&page=${page}`);
};

export const getBuyerContacts = (contractorUuid: string, page: number) => {
    return axios.get(`${prefix}GetBuyerContacts?contractorUuid=${contractorUuid}&page=${page}`);
};

export const addToShoppingCart = (item: IShopingCartProduct) => {
    return axios.put(`${prefix}AddToShoppingCart`, item);
};

export const removeProductById = (productId: string) => {
    return axios.delete(`${prefix}RemoveProductById/${productId}`);
};

export const removeAll = () => {
    return axios.delete(`${prefix}RemoveAll`);
};

export const updateQuantity = (productId: string, newQuantity: number) => {
    return axios.post(`${prefix}UpdateQuantity/${productId}/${newQuantity}`);
};

export const updateOrAddBuildingSite = (buildingSite: IBuildingSiteListViewDto) => {
    return axios.post(`${prefix}UpdateOrAddBuildingSite`, buildingSite);
};

export const updateShoppingCartAfterChangeBuildingSite = () => {
    return axios.post(`${prefix}UpdateShoppingCartAfterChangeBuildingSite`);
};

export const updateOrAddBuildingSiteAddress = (buildingSiteAddress: IBuildingSiteAddressListView) => {
    return axios.post(`${prefix}UpdateOrAddBuildingSiteAddress`, buildingSiteAddress);
};

export const updateOrAddBuyerContact = (buyerContact: IBuyerContactListView) => {
    return axios.post(`${prefix}UpdateOrAddBuyerContact`, buyerContact);
};

export const updateShoppingCartDetails = (detail: IShopingCartDetail) => {
    return axios.post(`${prefix}UpdateShoppingCartDetails`, detail);
};

export const reOpenOrderInShoppingCart = (documentId: string) => {
    const languageCode = localStorage.getItem('lang');
    return axios.post(`${prefix}ReOpenOrderInShoppingCart/${documentId}/${languageCode}`);
};

export const getExistOrderAsShoppingCart = (documentId: string) => {
    const languageCode = localStorage.getItem('lang');
    return axios.post(`${prefix}GetExistOrderAsShoppingCart/${documentId}/${languageCode}`);
};

export const getPdfOrderSummary = (
    documentId: string,
    documentNumber: string,
    documentDate: Date,
    documentStatusName: string
) => {
    const languageCode = localStorage.getItem('lang');
    return axios.get(
        `${prefix}GetPdfOrderSummary?documentId=${documentId}&languageCode=${languageCode}&documentNumber=${documentNumber}&documentDate=${documentDate}&documentStatusName=${documentStatusName}`
    );
};

export const getExcelOrderSummary = (
    documentId: string,
    documentNumber: string,
    documentDate: Date,
    documentStatusName: string
) => {
    const languageCode = localStorage.getItem('lang');
    return axios.get(
        `${prefix}GetExcelOrderSummary?documentId=${documentId}&languageCode=${languageCode}&documentNumber=${documentNumber}&documentDate=${documentDate}&documentStatusName=${documentStatusName}`
    );
};

export function uploadBondFile(dto: FormData) {
    return axios.post(`${prefix}UploadBondFile`, dto);
}

export const getTotalQuantityShoppingCart = (shopingCart: IShopingCart) => {
    return shopingCart.products.reduce((a, v) => (a = a + v.quantity), 0);
};

export const getShopingCartProduct = (
    product: FixcenterProduct,
    quantity: number,
    minQuantity: number,
    netPrice: number,
    brutPrice: number,
    discount: number,
    familyCode: string,
    imageFileId: string | null,
    productParentUuid?: string
) => {
    const salePackagingUnit: IProductPackagingDto =
        Array.isArray(product.salePackagingUnits) && product.salePackagingUnits.length > 0
            ? product.salePackagingUnits[0]
            : ({} as IProductPackagingDto);
    return {
        minQuantity: minQuantity,
        productChildUuid: product.id,
        productName: product.translation ? product.translation : product.name,
        quantity: quantity,
        productParentUuid: productParentUuid,
        articleCode: product.code,
        saleCurrencyName: product.saleCurrencyName,
        discount: discount,
        familyCode: familyCode,
        imageFileId: imageFileId,
        netto: netPrice,
        brutto: brutPrice,
        salesUnitId: !Utils.isNullOrUndefined(salePackagingUnit.unit) ? salePackagingUnit.unit : null,
    } as IShopingCartProduct;
};

export const getPriceWithPrecision = (value: number) => {
    return Utils.getNumberAsStringWithPrecision(value, roundFactor);
};

export const getNetPriceWithPrecision = (value: number) => {
    return Utils.getNumberAsStringWithPrecision(value, netPriceRoundFactor);
};

export const calculateTotalPricePerProduct = (price: number, quantity: number, isDisabled: boolean) => {
    if (isDisabled) {
        return 0;
    }
    let totalPrice: number = 0;
    if (price) {
        const value: number = price;
        totalPrice = value * quantity;
    }
    return Utils.getNumberAsStringWithPrecision(totalPrice, roundFactor);
};

export const mapAndGetInitialFormValues = (detail: IShopingCartDetail) => {
    if (detail) {
        return {
            reference: detail.reference,
            comments: detail.comments,
        } as CartOrderFormViewModel;
    }
    return new CartOrderFormViewModel();
};
